import { type RefObject, useEffect, useRef } from 'react'

import { resolveSwipeTargetPage } from './swipePageNavigation'

interface UseSwipePageNavigationOptions {
  containerRef: RefObject<HTMLElement | null>
  enabled: boolean
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  isSwipeAllowed: () => boolean
}

interface SwipeSession {
  startX: number
  startY: number
  startTime: number
  cancelled: boolean
}

export function useSwipePageNavigation({
  containerRef,
  enabled,
  currentPage,
  totalPages,
  onPageChange,
  isSwipeAllowed,
}: UseSwipePageNavigationOptions): void {
  const latestRef = useRef({
    currentPage,
    totalPages,
    onPageChange,
    isSwipeAllowed,
  })
  latestRef.current = { currentPage, totalPages, onPageChange, isSwipeAllowed }

  useEffect(() => {
    const container = containerRef.current
    if (!container || !enabled) return

    let session: SwipeSession | null = null

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        if (session) session.cancelled = true
        return
      }
      if (!latestRef.current.isSwipeAllowed()) {
        session = null
        return
      }
      const touch = event.touches.item(0)
      if (!touch) return
      session = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: performance.now(),
        cancelled: false,
      }
    }

    const onTouchEnd = (event: TouchEvent) => {
      const active = session
      session = null
      if (!active || active.cancelled) return
      if (event.touches.length > 0) return
      const touch = event.changedTouches.item(0)
      if (!touch) return

      const latest = latestRef.current
      const target = resolveSwipeTargetPage(
        {
          deltaX: touch.clientX - active.startX,
          deltaY: touch.clientY - active.startY,
          durationMs: performance.now() - active.startTime,
        },
        latest.currentPage,
        latest.totalPages
      )
      if (target !== null) latest.onPageChange(target)
    }

    const onTouchCancel = () => {
      session = null
    }

    container.addEventListener('touchstart', onTouchStart, { passive: true })
    container.addEventListener('touchend', onTouchEnd, { passive: true })
    container.addEventListener('touchcancel', onTouchCancel, { passive: true })
    return () => {
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchend', onTouchEnd)
      container.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [containerRef, enabled])
}
